import {
  Box,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { IconChart2 } from 'central-icons/IconChart2';
import { IconTable } from 'central-icons/IconTable';
import { forwardRef, type ReactNode, useMemo, useRef, useState } from 'react';

import { US_COUNTY_BY_FIPS } from '../data/us-geo';
import { US_STATE_BY_FIPS } from '../data/us-states';
import { fmtMult, fmtUSD } from '../format';
import type { SelectedGeo } from '../types';
import type { UseImpactScenariosResult } from '../useImpact';
import { ChartActions } from './ChartActions';
import { type CountyHeatDatum, EconomicImpactMap } from './EconomicImpactMap';
import {
  DII_COLOR,
  DII_LABEL,
  DISPOSITIONS,
  DiiLegend,
  type DiiRow,
  type DiiTotals,
  diiTotal,
  StackedDiiBars,
} from './StackedDiiBars';

const TOP_INDUSTRIES_COUNT = 7;
const TOP_COUNTIES_COUNT = 7;

// Short region label for a row's `region.fips`: counties drop the
// " County"/"Parish" suffix (design shows "DeKalb", "Fulton"); a
// BEA-padded state fips ("36000") resolves to the state name.
function regionLabel(fips: string): string {
  const county = US_COUNTY_BY_FIPS.get(fips);
  if (county) {
    return county.name.replace(
      / (County|Parish|Borough|Census Area|Municipality|city)$/,
      ''
    );
  }
  const state =
    US_STATE_BY_FIPS.get(fips) ?? US_STATE_BY_FIPS.get(fips.slice(0, 2));
  return state?.name ?? fips;
}

// Single full-width decomposition bar (design DecompBar): segments sized by
// share. The absolute values live in the legend underneath so the small
// indirect/induced amounts stay readable even when their segment is a sliver
// too thin to fit a number inside.
function ImpactDecompositionBar({ totals }: { totals: DiiTotals }) {
  const total = diiTotal(totals);
  return (
    <Box data-testid="impact-decomposition-bar">
      <Flex h="46px" gap="2px">
        {DISPOSITIONS.map((disposition, i) => {
          const pct = total > 0 ? (totals[disposition] / total) * 100 : 0;
          return (
            <Box
              key={disposition}
              width={`${pct}%`}
              bg={DII_COLOR[disposition]}
              borderLeftRadius={i === 0 ? '6px' : 0}
              borderRightRadius={i === DISPOSITIONS.length - 1 ? '6px' : 0}
              transition="width 0.45s ease"
              data-testid={`decomposition-${disposition}`}
            />
          );
        })}
      </Flex>
      <Flex gap="20px" mt="12px" flexWrap="wrap">
        {DISPOSITIONS.map((disposition) => (
          <Flex
            key={disposition}
            align="center"
            gap="7px"
            data-testid={`decomposition-value-${disposition}`}
          >
            <Box
              w="11px"
              h="11px"
              borderRadius="3px"
              bg={DII_COLOR[disposition]}
            />
            <Text fontSize="12px" fontWeight="500" color="navyBlue.500">
              {DII_LABEL[disposition]}
            </Text>
            <Text
              fontFamily="mono"
              fontWeight="700"
              fontSize="12px"
              color="navyBlue.500"
            >
              {fmtUSD(totals[disposition])}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}

// Topline (design ToplineRow): total + multiplier pill on the left, the
// direct/indirect/induced decomposition filling the rest of the line.
function ToplineCard({
  totals,
  isLoading,
}: {
  totals: DiiTotals;
  isLoading: boolean;
}) {
  const total = diiTotal(totals);
  const multiplier = totals.direct > 0 ? total / totals.direct : null;
  return (
    <Flex
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      p="16px 20px"
      gap="24px"
      flexShrink={0}
      data-testid="total-output-stat"
    >
      <Box
        w="250px"
        flexShrink={0}
        borderRight="1px solid"
        borderColor="gray.200"
        pr="24px"
      >
        <Text
          fontSize="11.5px"
          fontWeight="600"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="0.5px"
          mb="14px"
        >
          Total output
        </Text>
        <Flex align="center" gap="10px">
          <Text
            fontFamily="mono"
            fontWeight="700"
            fontSize="34px"
            color="navyBlue.500"
            letterSpacing="-1px"
            lineHeight="1"
          >
            {isLoading ? '—' : fmtUSD(total)}
          </Text>
          {!isLoading && multiplier !== null && (
            <Flex
              align="center"
              h="22px"
              px="10px"
              bg="green.50"
              border="1px solid"
              borderColor="green.200"
              borderRadius="full"
              data-testid="total-output-stat-badge"
            >
              <Text
                fontFamily="mono"
                fontWeight="700"
                fontSize="11.5px"
                lineHeight="1"
                color="green.700"
                whiteSpace="nowrap"
              >
                {fmtMult(multiplier)} mult.
              </Text>
            </Flex>
          )}
        </Flex>
        <Text fontSize="12px" color="gray.400" mt="10px">
          Direct + indirect + induced · 2018
        </Text>
      </Box>
      <Box flex="1" minW="0">
        <Text
          fontSize="11.5px"
          fontWeight="600"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="0.5px"
          mb="14px"
        >
          Impact breakdown
        </Text>
        <ImpactDecompositionBar totals={totals} />
      </Box>
    </Flex>
  );
}

// Chart ↔ table toggle (design ViewToggle).
function ViewToggle({
  mode,
  setMode,
  testIdPrefix,
}: {
  mode: 'chart' | 'table';
  setMode: (mode: 'chart' | 'table') => void;
  testIdPrefix: string;
}) {
  return (
    <Flex bg="gray.100" borderRadius="7px" p="3px" gap="2px">
      {(['chart', 'table'] as const).map((key) => (
        <IconButton
          key={key}
          aria-label={key === 'chart' ? 'Chart view' : 'Table view'}
          size="xs"
          variant="unstyled"
          minW="auto"
          h="auto"
          px="9px"
          py="4px"
          borderRadius="5px"
          display="flex"
          color={mode === key ? 'navyBlue.500' : 'gray.400'}
          bg={mode === key ? 'white' : 'transparent'}
          boxShadow={mode === key ? '0 1px 3px rgba(0,0,0,.12)' : 'none'}
          onClick={() => setMode(key)}
          data-testid={`${testIdPrefix}-view-${key}`}
          icon={
            key === 'chart' ? <IconChart2 size={15} /> : <IconTable size={15} />
          }
        />
      ))}
    </Flex>
  );
}

const Card = forwardRef<
  HTMLDivElement,
  {
    title: string;
    action?: ReactNode;
    children: ReactNode;
    flex?: string;
    testId?: string;
    opacity?: number;
  }
>(({ title, action, children, flex, testId, opacity }, ref) => {
  return (
    <Flex
      ref={ref}
      // `role="group"` drives the action menu's _groupHover reveal.
      role="group"
      direction="column"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      flex={flex}
      minH="0"
      opacity={opacity}
      data-testid={testId}
    >
      <Flex align="center" p="15px 18px 0" flexShrink={0}>
        <Text fontSize="15px" fontWeight="600" color="navyBlue.500">
          {title}
        </Text>
        <Box flex="1" />
        {action}
      </Flex>
      <Box p="18px" flex="1" minH="0" overflow="auto">
        {children}
      </Box>
    </Flex>
  );
});
Card.displayName = 'Card';

function DiiTable({
  rows,
  labelHead,
  testId,
  externalKeys = [],
  onRowHover,
  onRowLeave,
}: {
  rows: DiiRow[];
  labelHead: string;
  testId: string;
  /** Rows whose key is highlighted elsewhere (e.g. map hover) get a green wash. */
  externalKeys?: string[];
  onRowHover?: (row: DiiRow) => void;
  onRowLeave?: () => void;
}) {
  return (
    <TableContainer>
      <Table size="sm" data-testid={testId}>
        <Thead>
          <Tr>
            <Th>{labelHead}</Th>
            <Th isNumeric>Direct</Th>
            <Th isNumeric>Indirect</Th>
            <Th isNumeric>Induced</Th>
            <Th isNumeric>Total</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.key}
              bg={externalKeys.includes(row.key) ? 'green.50' : undefined}
              cursor={onRowHover ? 'pointer' : undefined}
              onMouseEnter={() => onRowHover?.(row)}
              onMouseLeave={() => onRowLeave?.()}
            >
              <Td
                maxW="200px"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {row.label}
              </Td>
              <Td isNumeric fontFamily="mono">
                {fmtUSD(row.direct)}
              </Td>
              <Td isNumeric fontFamily="mono">
                {fmtUSD(row.indirect)}
              </Td>
              <Td isNumeric fontFamily="mono">
                {fmtUSD(row.induced)}
              </Td>
              <Td isNumeric fontFamily="mono" fontWeight="700">
                {fmtUSD(diiTotal(row))}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

interface OutputTabProps {
  impactQuery: UseImpactScenariosResult;
  selectedGeo: SelectedGeo | null;
  onSelectGeo: (geo: SelectedGeo) => void;
  /**
   * No-scenario placeholder (variant 5): keep the results layout but dim the
   * topline + charts and drop the map into explore mode (empty stat tooltips)
   * so the page reads as "ready, awaiting a scenario" rather than empty.
   */
  placeholder?: boolean;
  /** Frame the map on the output geography rather than the impact heat (V4). */
  fitToSelected?: boolean;
}

export function OutputTab({
  impactQuery,
  selectedGeo,
  onSelectGeo,
  placeholder = false,
  fitToSelected = false,
}: OutputTabProps) {
  const { data, isLoading, error, failedScenarios, unsupportedScenarios } =
    impactQuery;
  const [industriesMode, setIndustriesMode] = useState<'chart' | 'table'>(
    'chart'
  );
  const [countiesMode, setCountiesMode] = useState<'chart' | 'table'>('chart');
  // Counties highlighted on the map — a single county (county hover) or all
  // counties an industry touches (industry hover).
  const [highlightedCounties, setHighlightedCounties] = useState<string[]>([]);
  const industriesCardRef = useRef<HTMLDivElement>(null);
  const countiesCardRef = useRef<HTMLDivElement>(null);

  // The topline comes straight from the API's economy-wide total (induced is
  // always 0), not a sum of the ranked rows — those are truncated to the top N.
  const outputByDisposition: DiiTotals = useMemo(
    () =>
      data
        ? {
            direct: data.total.direct,
            indirect: data.total.indirect,
            induced: data.total.induced,
          }
        : { direct: 0, indirect: 0, induced: 0 },
    [data]
  );

  // Top industries within the primary county (already ranked by the API).
  const topIndustries: DiiRow[] = useMemo(
    () =>
      (data?.industries ?? [])
        .slice(0, TOP_INDUSTRIES_COUNT)
        .map((industry) => ({
          key: industry.code,
          label: industry.label,
          direct: industry.direct,
          indirect: industry.indirect,
          induced: industry.induced,
        })),
    [data]
  );

  // Top counties nationally (already ranked by the API).
  const topCounties: DiiRow[] = useMemo(
    () =>
      (data?.counties ?? []).slice(0, TOP_COUNTIES_COUNT).map((county) => ({
        key: county.fips,
        label: regionLabel(county.fips),
        direct: county.direct,
        indirect: county.indirect,
        induced: county.induced,
      })),
    [data]
  );

  // Per-county breakdown (D/I/I + that county's top industries) driving the
  // map's green heat and hover tooltip. Counties only — a state-level region
  // fips has no county geometry to shade.
  const countyData = useMemo(() => {
    const result = new Map<string, CountyHeatDatum>();
    for (const county of data?.counties ?? []) {
      if (!US_COUNTY_BY_FIPS.has(county.fips)) continue;
      result.set(county.fips, {
        name:
          US_COUNTY_BY_FIPS.get(county.fips)?.name ?? regionLabel(county.fips),
        direct: county.direct,
        indirect: county.indirect,
        induced: county.induced,
        topIndustries: county.industries.slice(0, 4).map((industry) => ({
          label: industry.label,
          direct: industry.direct,
          indirect: industry.indirect,
          induced: industry.induced,
        })),
      });
    }
    return result;
  }, [data]);

  // Industry code → the county fips it lands in, so hovering an industry can
  // light up every county it touches on the map (keyed on the DiiRow `key`).
  const industryToCounties = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const county of data?.counties ?? []) {
      if (!US_COUNTY_BY_FIPS.has(county.fips)) continue;
      for (const industry of county.industries) {
        const set = map.get(industry.code) ?? new Set<string>();
        set.add(county.fips);
        map.set(industry.code, set);
      }
    }
    return map;
  }, [data]);

  // Industry code → number of contributing counties, surfaced in the bar
  // tooltip (mirrors the map cross-highlight count).
  const industryCountyCounts = useMemo(
    () => new Map([...industryToCounties].map(([key, set]) => [key, set.size])),
    [industryToCounties]
  );

  if (error) {
    return (
      <Flex flex="1" align="center" justify="center" direction="column" gap={1}>
        <Text fontSize="13px" fontWeight="600" color="text.primary">
          No impact data available
        </Text>
        <Text fontSize="12px" color="gray.500" data-testid="output-tab-error">
          {error.message}
        </Text>
      </Flex>
    );
  }

  // Every added scenario is a grain the model can't run yet (e.g. states):
  // show a clear "not modeled" state rather than an all-zero result.
  if (!isLoading && !data && unsupportedScenarios.length > 0) {
    return (
      <Flex
        flex="1"
        align="center"
        justify="center"
        direction="column"
        gap={1}
        data-testid="output-tab"
      >
        <Text fontSize="13px" fontWeight="600" color="text.primary">
          Not modeled yet
        </Text>
        <Text
          fontSize="12px"
          color="gray.500"
          data-testid="unsupported-scenario-banner"
        >
          State-level impact isn't supported yet — select a county to model it.{' '}
          {unsupportedScenarios
            .map((s) => US_STATE_BY_FIPS.get(s.fips)?.name ?? s.fips)
            .join(', ')}
        </Text>
      </Flex>
    );
  }

  const highlightIndustry = (row: DiiRow) =>
    setHighlightedCounties([...(industryToCounties.get(row.key) ?? [])]);
  const highlightCounty = (row: DiiRow) => setHighlightedCounties([row.key]);
  const clearHighlight = () => setHighlightedCounties([]);

  const industriesAction = (
    <Flex align="center" gap="14px">
      <DiiLegend />
      <ViewToggle
        mode={industriesMode}
        setMode={setIndustriesMode}
        testIdPrefix="industries"
      />
      <ChartActions
        title="Top industries impacted"
        rows={topIndustries}
        labelHead="Industry"
        labelWidth={210}
        filename="top-industries-impacted"
        captureRef={industriesCardRef}
      />
    </Flex>
  );
  const countiesAction = (
    <Flex align="center" gap="14px">
      <DiiLegend />
      <ViewToggle
        mode={countiesMode}
        setMode={setCountiesMode}
        testIdPrefix="counties"
      />
      <ChartActions
        title="Top counties impacted"
        rows={topCounties}
        labelHead="County"
        labelWidth={150}
        filename="top-counties-impacted"
        captureRef={countiesCardRef}
      />
    </Flex>
  );

  return (
    <Flex
      direction="column"
      flex="1"
      minH="0"
      gap="14px"
      overflow="auto"
      data-testid="output-tab"
    >
      {failedScenarios.length > 0 && (
        <Box
          bg="orange.50"
          border="1px solid"
          borderColor="orange.200"
          borderRadius="md"
          px={3}
          py={2}
          flexShrink={0}
          data-testid="scenario-failure-banner"
        >
          <Text fontSize="11px" color="orange.800" fontWeight="600">
            {failedScenarios.length === 1
              ? '1 scenario returned no data'
              : `${failedScenarios.length} scenarios returned no data`}
            :{' '}
            <Text as="span" fontWeight="400">
              {failedScenarios.map((f) => f.industryId).join(', ')}
            </Text>
          </Text>
        </Box>
      )}

      {unsupportedScenarios.length > 0 && (
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="md"
          px={3}
          py={2}
          flexShrink={0}
          data-testid="unsupported-scenario-banner"
        >
          <Text fontSize="11px" color="blue.800" fontWeight="600">
            State-level impact isn't supported yet — select a county to model
            it.{' '}
            <Text as="span" fontWeight="400">
              {unsupportedScenarios
                .map((s) => US_STATE_BY_FIPS.get(s.fips)?.name ?? s.fips)
                .join(', ')}
            </Text>
          </Text>
        </Box>
      )}

      <Box opacity={placeholder ? 0.55 : 1}>
        <ToplineCard
          totals={outputByDisposition}
          isLoading={isLoading || placeholder || !data}
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={{ base: '1fr', lg: '0.85fr 1.15fr' }}
        gap="14px"
        flex="1"
        minH="480px"
      >
        {/* Impact by county — green heat over the impacted counties */}
        <Card title="Impact by county" testId="impact-by-county-card">
          <Box position="relative" w="100%" h="100%" minH="360px">
            <EconomicImpactMap
              selectedGeo={selectedGeo}
              onSelectGeo={onSelectGeo}
              counties={placeholder ? undefined : countyData}
              highlightedFips={highlightedCounties}
              onHoverCounty={(fips) =>
                setHighlightedCounties(fips ? [fips] : [])
              }
              fitToSelected={fitToSelected}
              // Output map: heat + selected-geo outline only, no green fill.
              highlightSelection={false}
              // Output geography is changed via the title dropdown, not the map.
              selectable={false}
            />
          </Box>
        </Card>

        <Flex direction="column" gap="14px" minH="0" position="relative">
          {placeholder && (
            <Flex
              position="absolute"
              inset="0"
              align="center"
              justify="center"
              zIndex={1}
              pointerEvents="none"
              data-testid="output-placeholder-hint"
            >
              <Text
                fontSize="13px"
                fontWeight="600"
                color="gray.500"
                bg="white"
                px="14px"
                py="8px"
                borderRadius="8px"
                boxShadow="0 2px 10px rgba(20,30,55,.12)"
              >
                Add a scenario to model its economic impact
              </Text>
            </Flex>
          )}
          <Card
            ref={industriesCardRef}
            title="Top industries impacted"
            flex="1"
            action={industriesAction}
            testId="top-industries-card"
            opacity={placeholder ? 0.5 : 1}
          >
            {industriesMode === 'chart' ? (
              <StackedDiiBars
                rows={topIndustries}
                labelWidth={210}
                countByKey={industryCountyCounts}
                onRowHover={highlightIndustry}
                onRowLeave={clearHighlight}
              />
            ) : (
              <DiiTable
                rows={topIndustries}
                labelHead="Industry"
                testId="industries-table"
                onRowHover={highlightIndustry}
                onRowLeave={clearHighlight}
              />
            )}
          </Card>
          <Card
            ref={countiesCardRef}
            title="Top counties impacted"
            flex="1"
            action={countiesAction}
            testId="top-counties-card"
            opacity={placeholder ? 0.5 : 1}
          >
            {countiesMode === 'chart' ? (
              <StackedDiiBars
                rows={topCounties}
                labelWidth={150}
                externalKeys={highlightedCounties}
                onRowHover={highlightCounty}
                onRowLeave={clearHighlight}
              />
            ) : (
              <DiiTable
                rows={topCounties}
                labelHead="County"
                testId="counties-table"
                externalKeys={highlightedCounties}
                onRowHover={highlightCounty}
                onRowLeave={clearHighlight}
              />
            )}
          </Card>
        </Flex>
      </Box>
    </Flex>
  );
}
